import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import userPreferencesManager from './UserPreferencesManager';
import {
  Target, BookOpen, Lightbulb, MessageCircle, Share2, Crown, Star,
  Settings, User, Globe, Mic, Bell, LogOut, Menu, X,
  ChevronLeft, ChevronRight, Check, Zap, Sparkles,
  GraduationCap, Heart, Coffee, Rocket, Diamond, Users,
  TrendingUp, Search, Plus, Video, Home, Brain, Compass,
  CreditCard, BarChart3, MapPin, Cpu, Save, AlertCircle, Camera
} from 'lucide-react';

const AladinnNavigation = ({ 
  user, 
  onNavigate, 
  currentSection, 
  currentCompetence, 
  onLogout,
  onSidebarToggle,
  sidebarCollapsed
}) => {
  // États pour la responsivité
  const [menuCollapsed, setMenuCollapsed] = useState(sidebarCollapsed || false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // États pour les modales
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  
  // États pour la gestion des préférences
  const [userPreferences, setUserPreferences] = useState(null);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesError, setPreferencesError] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  const auth = getAuth();

  // Synchroniser avec l'état parent
  useEffect(() => {
    setMenuCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Détecter la taille d'écran
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const newIsMobile = width < 768;
      const newIsTablet = width >= 768 && width < 1024;
      
      setIsMobile(newIsMobile);
      setIsTablet(newIsTablet);
      
      // Auto-collapse sur mobile/tablet
      if (newIsMobile || newIsTablet) {
        setMenuCollapsed(false);
        setMobileMenuOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fermer le menu mobile quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-nav-container') && !event.target.closest('.mobile-toggle-button')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // Empêcher le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (mobileMenuOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen, isMobile]);

  // Profil utilisateur simplifié
  const userProfile = {
    name: user?.displayName || user?.email || "Utilisateur",
    plan: "Découverte",
    usage: {
      objectives: { current: 3, max: 5 },
      searches: { current: 45, max: 100 },
      storage: { current: 2.3, max: 5.0 }
    }
  };

  // Structure de navigation simplifiée et responsive
  const simplifiedMenuStructure = {
    parcours: [
      { 
        icon: Target, 
        label: 'Stratégie', 
        id: 'objectif',
        subsections: ['competences', 'details']
      },
      { 
        icon: Lightbulb, 
        label: 'Inspiration', 
        id: 'inspiration',
        subsections: ['inspiration', 'webSource', 'webSearch']
      },
      { 
        icon: BookOpen, 
        label: 'Exploration', 
        id: 'exploration',
        subsections: ['exploration', 'cours', 'knowledgeBase']
      },
      { 
        icon: GraduationCap, 
        label: 'Action', 
        id: 'actionSpace',
        subsections: ['competencesList', 'actionPlan']
      },
      { 
        icon: BarChart3, 
        label: 'Tableau de Bord', 
        id: 'tableau-de-bord',
        subsections: ['progression']
      }
    ],
    social: [
      { icon: Cpu, label: 'Ferme Larache', id: 'farmroad' },
      { icon: Camera, label: 'Analyse Images IA', id: 'farmroad-images' },
      { icon: Users, label: 'Home', id: 'HomePage' }
    ],
    information: [
      { icon: TrendingUp, label: 'News', id: 'actualites' },
      { icon: Video, label: 'Learning', id: 'academy' }
    ],
    outils: [
      { icon: MessageCircle, label: 'Chat Aladinn', id: 'chat' },
      { icon: Share2, label: 'Sharing', id: 'partage' }
    ]
  };

  // Charger les préférences utilisateur au montage
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!auth.currentUser) return;

      setPreferencesLoading(true);
      setPreferencesError(null);

      try {
        const preferences = await userPreferencesManager.getUserPreferences(auth.currentUser.uid);
        setUserPreferences(preferences);
        console.log('Préférences utilisateur chargées:', preferences);
      } catch (error) {
        console.error('Erreur lors du chargement des préférences:', error);
        setPreferencesError('Impossible de charger vos préférences');
        setUserPreferences(userPreferencesManager.getDefaultPreferences());
      } finally {
        setPreferencesLoading(false);
      }
    };

    loadUserPreferences();
  }, [auth.currentUser]);

  // Sauvegarder les préférences
  const savePreferences = async (newPreferences) => {
    if (!auth.currentUser) return;

    setPreferencesSaving(true);
    setPreferencesError(null);

    try {
      const savedPreferences = await userPreferencesManager.saveUserPreferences(
        auth.currentUser.uid, 
        newPreferences
      );
      setUserPreferences(savedPreferences);
      setUnsavedChanges(false);
      console.log('Préférences sauvegardées avec succès');
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setPreferencesError('Erreur lors de la sauvegarde des préférences');
      return false;
    } finally {
      setPreferencesSaving(false);
    }
  };

  // Gestion des changements de préférences
  const handlePreferenceChange = (section, field, value) => {
    if (!userPreferences) return;

    const updatedPreferences = { ...userPreferences };
    
    if (section) {
      if (!updatedPreferences[section]) {
        updatedPreferences[section] = {};
      }
      updatedPreferences[section][field] = value;
    } else {
      updatedPreferences[field] = value;
    }

    setUserPreferences(updatedPreferences);
    setUnsavedChanges(true);
  };

  // Navigation avec fermeture automatique du menu mobile
  const handleNavigation = (sectionId) => {
    onNavigate(sectionId);
    setMobileMenuOpen(false);
  };

  // Gestion du toggle avec communication vers le parent
  const toggleMenu = () => {
    if (isMobile || isTablet) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      const newCollapsed = !menuCollapsed;
      setMenuCollapsed(newCollapsed);
      // Communiquer le changement au composant parent
      if (onSidebarToggle) {
        onSidebarToggle(newCollapsed);
      }
    }
  };

  // Modal des paramètres responsive
  const SettingsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 lg:p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Paramètres et préférences</h2>
              <p className="text-gray-600 mt-1 text-sm lg:text-base">Configurez votre expérience Aladinn</p>
              {unsavedChanges && (
                <div className="mt-2 flex items-center text-amber-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  <span className="text-sm">Modifications non sauvegardées</span>
                </div>
              )}
            </div>
            <button 
              onClick={() => setShowSettingsModal(false)}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {preferencesError && (
          <div className="mx-4 lg:mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center text-red-700">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{preferencesError}</span>
            </div>
          </div>
        )}

        <div className="p-4 lg:p-6 space-y-6">
          {/* Localisation */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Localisation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Langue
                </label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm lg:text-base"
                  value={userPreferences?.language || 'fr'}
                  onChange={(e) => handlePreferenceChange(null, 'language', e.target.value)}
                  disabled={preferencesLoading}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Pays
                </label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm lg:text-base"
                  value={userPreferences?.country || 'MA'}
                  onChange={(e) => handlePreferenceChange(null, 'country', e.target.value)}
                  disabled={preferencesLoading}
                >
                  <option value="MA">Maroc</option>
                  <option value="FR">France</option>
                  <option value="CA">Canada</option>
                  <option value="BE">Belgique</option>
                  <option value="CH">Suisse</option>
                  <option value="US">États-Unis</option>
                  <option value="ES">Espagne</option>
                  <option value="DE">Allemagne</option>
                  <option value="IT">Italie</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 lg:p-6 border-t bg-gray-50 flex flex-col lg:flex-row justify-between items-center gap-3">
          <div className="flex items-center text-sm text-gray-600">
            {preferencesLoading && (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                Chargement...
              </>
            )}
            {preferencesSaving && (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                Sauvegarde en cours...
              </>
            )}
          </div>
          
          <div className="flex space-x-3 w-full lg:w-auto">
            <button 
              onClick={() => setShowSettingsModal(false)}
              className="flex-1 lg:flex-none px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={preferencesSaving}
            >
              Annuler
            </button>
            <button 
              onClick={async () => {
                const success = await savePreferences(userPreferences);
                if (success) {
                  setShowSettingsModal(false);
                }
              }}
              className="flex-1 lg:flex-none px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center disabled:opacity-50"
              disabled={preferencesSaving || !unsavedChanges}
            >
              {preferencesSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Modal compte responsive
  const AccountModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 lg:p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Mon compte Aladinn</h2>
              <p className="text-gray-600 mt-1 text-sm lg:text-base">Gérez votre abonnement</p>
            </div>
            <button 
              onClick={() => setShowAccountModal(false)}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 lg:p-6 space-y-6">
          {/* Plan actuel */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan actuel</h3>
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 lg:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-3">
                <div className="flex items-center space-x-3 mb-3 lg:mb-0">
                  <Coffee className="w-8 h-8 text-amber-500 flex-shrink-0" />
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">Plan Découverte</h4>
                    <p className="text-gray-600">Expérimental</p>
                  </div>
                </div>
                <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 w-full lg:w-auto">
                  Modifier le plan
                </button>
              </div>
            </div>
          </div>

          {/* Autres plans */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Autres plans disponibles</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Heart className="w-6 h-6 text-pink-500" />
                  <span className="text-lg font-bold">19€/mois</span>
                </div>
                <h4 className="font-semibold mb-2">Explorateur</h4>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• 10 objectif actif</li>
                  <li>• 100 recherches/mois</li>
                  <li>• 1 GB stockage</li>
                </ul>
                <button className="w-full py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Mettre à niveau
                </button>
              </div>

              <div className="border-2 border-purple-500 rounded-lg p-4 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Populaire
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <Rocket className="w-6 h-6 text-purple-500" />
                  <span className="text-lg font-bold">49€/mois</span>
                </div>
                <h4 className="font-semibold mb-2">Innovateur</h4>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Objectifs illimités</li>
                  <li>• Recherches illimitées</li>
                  <li>• 20 GB stockage</li>
                  <li>• Analytics avancés</li>
                </ul>
                <button className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Mettre à niveau
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Diamond className="w-6 h-6 text-blue-500" />
                  <span className="text-lg font-bold">Sur mesure</span>
                </div>
                <h4 className="font-semibold mb-2">Entreprise</h4>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Multi-utilisateurs</li>
                  <li>• IA personnalisée</li>
                  <li>• Stockage illimité</li>
                  <li>• Support dédié</li>
                </ul>
                <button className="w-full py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Nous contacter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Modals */}
      {showSettingsModal && <SettingsModal />}
      {showAccountModal && <AccountModal />}

      {/* Bouton mobile toggle - RESPONSIVE */}
      <button
        className={`mobile-toggle-button lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors ${mobileMenuOpen ? 'bg-gray-200' : ''}`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay mobile */}
      {mobileMenuOpen && (isMobile || isTablet) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Menu principal - VERSION RESPONSIVE CORRIGÉE */}
      <div 
        className={`mobile-nav-container fixed top-0 left-0 h-screen bg-white shadow-xl z-40
                   transition-all duration-300 ease-in-out overflow-hidden flex flex-col
                   ${isMobile || isTablet ? 
                     `w-80 max-w-[85vw] ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}` : 
                     `${menuCollapsed ? 'w-16' : 'w-64'} translate-x-0`
                   }`}
      >
        {/* En-tête responsive */}
        <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0">
          <div className={`flex items-center ${menuCollapsed && !isMobile && !isTablet ? 'justify-center w-full' : ''}`}>
            <Sparkles className="w-8 h-8 text-purple-600 flex-shrink-0" /> 
            {(!menuCollapsed || isMobile || isTablet) && (
              <span className="ml-3 font-bold text-xl text-purple-600">Aladinn</span>
            )}
          </div>
          
          {/* Bouton collapse pour desktop uniquement */}
          {!isMobile && !isTablet && (
            <button
              onClick={toggleMenu}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              {menuCollapsed ? (
                <ChevronRight className="w-5 h-5 text-gray-600" />
              ) : (
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              )}
            </button>
          )}
        </div>

        {/* Zone scrollable du contenu */}
        <div className="flex-1 overflow-y-auto">
          {/* Profil utilisateur responsive */}
          {user && (!menuCollapsed || isMobile || isTablet) && (
            <div className="p-4 border-b border-gray-200">
              <div className="text-center">
                <h3 className="font-semibold text-gray-900 truncate">{userProfile.name}</h3>
                <p className="text-sm text-gray-600">Plan {userProfile.plan}</p>
                {unsavedChanges && (
                  <div className="mt-1 flex items-center justify-center text-xs text-amber-600">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    <span>Paramètres modifiés</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation responsive */}
          <div className="p-3">
            {/* Services */}
            <div className="mb-6">
              {(!menuCollapsed || isMobile || isTablet) && (
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
                  Services
                </h4>
              )}
              <div className="space-y-1">
                {simplifiedMenuStructure.social.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className="w-full flex items-center p-3 rounded-lg text-gray-600 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    title={menuCollapsed && !isMobile && !isTablet ? item.label : ''}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {(!menuCollapsed || isMobile || isTablet) && (
                      <span className="font-medium truncate">{item.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Informations */}
            <div className="mb-6">
              {(!menuCollapsed || isMobile || isTablet) && (
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
                  Informations
                </h4>
              )}
              <div className="space-y-1">
                {simplifiedMenuStructure.information.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className="w-full flex items-center p-3 rounded-lg text-gray-600 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    title={menuCollapsed && !isMobile && !isTablet ? item.label : ''}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {(!menuCollapsed || isMobile || isTablet) && (
                      <span className="font-medium truncate">{item.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Outils */}
            <div className="mb-6">
              {(!menuCollapsed || isMobile || isTablet) && (
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
                  Outils
                </h4>
              )}
              <div className="space-y-1">
                {simplifiedMenuStructure.outils.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className="w-full flex items-center p-3 rounded-lg text-gray-600 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    title={menuCollapsed && !isMobile && !isTablet ? item.label : ''}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    {(!menuCollapsed || isMobile || isTablet) && (
                      <span className="font-medium truncate">{item.label}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Actions du bas - RESPONSIVE avec espace suffisant pour mobile */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white">
          {(!menuCollapsed || isMobile || isTablet) && (
            <div className={`p-4 space-y-2 ${isMobile ? 'pb-20' : ''}`}>
              <button 
                onClick={() => setShowAccountModal(true)}
                className="w-full flex items-center p-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <CreditCard className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="text-sm truncate">Mon abonnement</span>
              </button>
              <button 
                onClick={() => setShowSettingsModal(true)}
                className="w-full flex items-center p-2 rounded-lg text-gray-600 hover:bg-gray-50 relative transition-colors"
              >
                <Settings className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="text-sm truncate">Paramètres</span>
                {unsavedChanges && (
                  <div className="absolute right-2 w-2 h-2 bg-amber-500 rounded-full"></div>
                )}
              </button>
              <button 
                onClick={onLogout}
                className="w-full flex items-center p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="text-sm truncate">Se déconnecter</span>
              </button>
            </div>
          )}

          {/* Version collapsed pour desktop */}
          {menuCollapsed && !isMobile && !isTablet && (
            <div className="p-2 space-y-1">
              <button 
                onClick={() => setShowAccountModal(true)}
                className="w-full flex items-center justify-center p-2 rounded-lg text-gray-600 hover:bg-gray-50"
                title="Mon abonnement"
              >
                <CreditCard className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setShowSettingsModal(true)}
                className="w-full flex items-center justify-center p-2 rounded-lg text-gray-600 hover:bg-gray-50 relative"
                title="Paramètres"
              >
                <Settings className="w-4 h-4" />
                {unsavedChanges && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></div>
                )}
              </button>
              <button 
                onClick={onLogout}
                className="w-full flex items-center justify-center p-2 rounded-lg text-red-600 hover:bg-red-50"
                title="Se déconnecter"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AladinnNavigation;